import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getTrip } from '@/services/tripService'
import {
  listBookings,
  createBooking,
  updateBooking,
  deleteBooking,
  uploadBookingAttachment,
  getBookingAttachmentUrl,
  deleteBookingAttachment,
  type Booking,
} from '@/services/bookingService'
import { BookingForm } from '@/components/BookingForm'
import type { NewBookingFormValues, NewBookingFormInput } from '@/utils/schemas'

// `<input type="datetime-local">` only accepts "YYYY-MM-DDTHH:mm" — the
// stored value is a full ISO string with a UTC offset (e.g.
// "2026-10-01T08:30:00+00:00"), which the input silently rejects and shows
// blank. Booking times are treated as UTC wall-clock throughout this page
// (see formatDateTime below), so slice rather than convert to local time.
function toDateTimeLocalValue(value: string | null): string {
  if (!value) return ''
  return value.slice(0, 16)
}

function bookingToFormInput(booking: Booking): NewBookingFormInput {
  return {
    type: booking.type as NewBookingFormInput['type'],
    reference: booking.reference ?? '',
    startAt: toDateTimeLocalValue(booking.start_at),
    endAt: toDateTimeLocalValue(booking.end_at),
  }
}

function formatDateTime(value: string | null): string | null {
  if (!value) return null
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value))
}

export function BookingsPage() {
  const { t } = useTranslation()
  const { tripId } = useParams<{ tripId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingBookingId, setEditingBookingId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [uploadingId, setUploadingId] = useState<string | null>(null)
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const bookingsQuery = useQuery({
    queryKey: ['bookings', tripId],
    queryFn: () => listBookings(tripId!),
    enabled: !!tripId,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['bookings', tripId] })

  const addBookingMutation = useMutation({
    mutationFn: (values: NewBookingFormValues) => createBooking({ tripId: tripId!, ...values }),
    onSuccess: async () => {
      await invalidate()
      setShowAddForm(false)
    },
  })

  const updateBookingMutation = useMutation({
    mutationFn: ({ bookingId, values }: { bookingId: string; values: NewBookingFormValues }) =>
      updateBooking(bookingId, values),
    onSuccess: async () => {
      await invalidate()
      setEditingBookingId(null)
    },
  })

  const deleteBookingMutation = useMutation({
    mutationFn: (bookingId: string) => deleteBooking(bookingId),
    onSuccess: async () => {
      await invalidate()
      setConfirmDeleteId(null)
    },
  })

  const uploadMutation = useMutation({
    mutationFn: ({ bookingId, file }: { bookingId: string; file: File }) =>
      uploadBookingAttachment(tripId!, bookingId, file),
    onSuccess: async () => {
      await invalidate()
      setUploadingId(null)
    },
    onError: () => setUploadingId(null),
  })

  const deleteAttachmentMutation = useMutation({
    mutationFn: ({ bookingId, path }: { bookingId: string; path: string }) =>
      deleteBookingAttachment(bookingId, path),
    onSuccess: invalidate,
  })

  const downloadAttachment = async (path: string) => {
    // Open the tab synchronously on click — opening it after the `await`
    // below resolves gets blocked as a popup by every major browser.
    const newTab = window.open('', '_blank', 'noreferrer')
    try {
      const url = await getBookingAttachmentUrl(path)
      if (newTab) newTab.location.href = url
    } catch {
      newTab?.close()
    }
  }

  if (tripQuery.isLoading || bookingsQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('bookings.loading')}</p>
  }

  const trip = tripQuery.data
  const bookings = bookingsQuery.data ?? []

  return (
    <div className="min-h-screen bg-night px-10 py-7">
      <button
        type="button"
        onClick={() => navigate(`/trips/${tripId}/itinerary`)}
        className="mb-3.5 font-sans text-sm text-mist"
      >
        {t('bookings.backToItinerary')}
      </button>
      <h1 className="font-display text-3xl text-ivory">{t('bookings.title')}</h1>
      <div className="mt-1.5 font-mono text-xs text-brass-dim">{trip?.title}</div>

      <div className="mt-8 rounded-lg border border-line bg-panel p-6">
        {bookings.length === 0 && <p className="mb-4 text-sm text-mist">{t('bookings.empty')}</p>}

        {bookings.map((booking) =>
          editingBookingId === booking.id ? (
            <div key={booking.id} className="mb-3.5">
              <BookingForm
                defaultValues={bookingToFormInput(booking)}
                isPending={updateBookingMutation.isPending}
                isError={updateBookingMutation.isError}
                submitLabel={t('bookings.save')}
                onCancel={() => setEditingBookingId(null)}
                onSubmit={(values) =>
                  updateBookingMutation.mutate({ bookingId: booking.id, values })
                }
              />
            </div>
          ) : (
            <div
              key={booking.id}
              className="mb-3.5 rounded-md border border-line bg-panel-2 px-4 py-3.5"
            >
              <div className="flex items-center justify-between gap-2.5">
                <span className="font-sans text-[15px] font-medium text-ivory">
                  {t(`bookings.type.${booking.type}`)}
                </span>
                {booking.reference && (
                  <span className="font-mono text-xs text-brass-dim">{booking.reference}</span>
                )}
              </div>
              {(booking.start_at || booking.end_at) && (
                <div className="mt-1 text-sm text-mist">
                  {formatDateTime(booking.start_at)}
                  {booking.end_at && ` → ${formatDateTime(booking.end_at)}`}
                </div>
              )}

              <div className="mt-2.5 flex flex-wrap items-center gap-3">
                {booking.attachment_path ? (
                  <>
                    <button
                      type="button"
                      onClick={() => downloadAttachment(booking.attachment_path!)}
                      className="font-sans text-xs text-mist hover:text-brass"
                    >
                      {t('bookings.downloadAttachment')}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        deleteAttachmentMutation.mutate({
                          bookingId: booking.id,
                          path: booking.attachment_path!,
                        })
                      }
                      disabled={deleteAttachmentMutation.isPending}
                      className="font-sans text-xs text-mist hover:text-brass-warm"
                    >
                      {t('bookings.removeAttachment')}
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      ref={(el) => {
                        fileInputRefs.current[booking.id] = el
                      }}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          setUploadingId(booking.id)
                          uploadMutation.mutate({ bookingId: booking.id, file })
                        }
                        e.target.value = ''
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRefs.current[booking.id]?.click()}
                      disabled={uploadingId === booking.id}
                      className="font-sans text-xs text-mist hover:text-brass disabled:opacity-50"
                    >
                      {uploadingId === booking.id
                        ? t('bookings.uploading')
                        : t('bookings.addAttachment')}
                    </button>
                  </>
                )}
              </div>

              {confirmDeleteId === booking.id ? (
                <div className="mt-2.5 rounded-r border-l-[3px] border-line border-l-brass-warm bg-panel px-3 py-2.5">
                  {deleteBookingMutation.isError && (
                    <p role="alert" className="mb-2 text-sm text-brass-warm">
                      {t('bookings.deleteError')}
                    </p>
                  )}
                  <p className="mb-2 text-sm text-ivory">{t('bookings.confirmDelete')}</p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="rounded border border-line-strong px-3 py-1.5 font-sans text-xs text-ivory"
                    >
                      {t('bookings.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteBookingMutation.mutate(booking.id)}
                      disabled={deleteBookingMutation.isPending}
                      className="rounded border border-brass-warm px-3 py-1.5 font-sans text-xs font-medium text-brass-warm disabled:opacity-50"
                    >
                      {t('bookings.confirmDeleteButton')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2.5 flex gap-4">
                  <button
                    type="button"
                    onClick={() => setEditingBookingId(booking.id)}
                    className="font-sans text-xs text-mist hover:text-brass"
                  >
                    {t('bookings.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(booking.id)}
                    className="font-sans text-xs text-mist hover:text-brass-warm"
                  >
                    {t('bookings.delete')}
                  </button>
                </div>
              )}
            </div>
          ),
        )}

        {showAddForm ? (
          <BookingForm
            isPending={addBookingMutation.isPending}
            isError={addBookingMutation.isError}
            submitLabel={t('bookings.addBooking')}
            onCancel={() => setShowAddForm(false)}
            onSubmit={(values) => addBookingMutation.mutate(values)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="mt-4 rounded-md border border-dashed border-line-strong px-4 py-3 text-left font-sans text-sm text-mist hover:border-brass hover:text-brass"
          >
            {t('bookings.addBooking')}
          </button>
        )}
      </div>
    </div>
  )
}
